import { useRootStore } from "@/state/RootStore";
import { ALL_STORAGE_KEYS } from "./storage"; // array of all your localStorage keys to clear

const DEVICE_ID_KEY = "DEVICE_ID";
const DEVICE_ID_SUFFIX_KEY = "DEVICE_ID_SUFFIX";

/**
 * Returns a stable “device” identifier for this browser.
 * - On first run it generates a UUID and persists it.
 * - Appends a suffix that you can bump via resetDeviceId.
 * Later you can swap this out to return your Auth0 user ID instead.
 */
export const getDeviceId = async (): Promise<string> => {
  // Get or create a base ID
  let baseId = localStorage.getItem(DEVICE_ID_KEY);
  if (!baseId) {
    // crypto.randomUUID() is preferred when available
    baseId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : // fallback to a simple random string
          Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(DEVICE_ID_KEY, baseId);
  }

  // 2) Check for a suffix
  const suffix = localStorage.getItem(DEVICE_ID_SUFFIX_KEY);
  return suffix ? `${baseId}-${suffix}` : baseId;
};

/**
 * Hook to bump the DEVICE_ID_SUFFIX, clear all app storage,
 * and reset your MobX root store.
 */
export const useResetDeviceId = () => {
  const store = useRootStore();

  const resetDeviceId = async (): Promise<void> => {
    // read current suffix
    const suffix = localStorage.getItem(DEVICE_ID_SUFFIX_KEY);

    // clear all your persisted keys
    ALL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

    // bump or initialize suffix
    const next = suffix ? `${Number(suffix) + 1}` : "1";
    localStorage.setItem(DEVICE_ID_SUFFIX_KEY, next);

    // reset your app state
    store.reset();
  };

  return { resetDeviceId };
};
